import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material';
import { updateBusinessSummaryExpress } from 'lib/api/express';
import CustomButton from 'lib/designSystem/Button';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import {
  communes,
  provinces,
  regions,
} from '../../../../BusinessProfile/Profile/components/Client/components/constants';

interface LocationEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: LocationData) => void;
  initialData: LocationData;
}

interface LocationData {
  region?: string;
  province?: string;
  commune?: string;
  address?: string;
}

interface LocationOption {
  id: number;
  name: string;
}

const LocationEditDialog: React.FC<LocationEditDialogProps> = ({
  open,
  onClose,
  onSave,
  initialData,
}) => {
  const [formData, setFormData] = useState<LocationData>(initialData);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);

  const findOptionByName = (
    options: LocationOption[],
    name: string | undefined,
  ) => {
    return options.find((option) => option.name === name) ?? null;
  };

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const handleSave = async () => {
    if (!user?.clientId || !user?.businessIdentifier) {
      showAlert(
        dispatch,
        'error',
        'No se ha encontrado la información de la empresa',
      );
      return;
    }

    if (
      !formData.region &&
      !formData.province &&
      !formData.commune &&
      !formData.address
    ) {
      showAlert(
        dispatch,
        'error',
        'Debe completar al menos un campo de ubicación',
      );
      return;
    }

    setLoading(true);
    try {
      await updateBusinessSummaryExpress(
        dispatch,
        user.clientId,
        user.businessIdentifier,
        formData,
      );

      onSave(formData);
      showAlert(
        dispatch,
        'success',
        'Información de ubicación actualizada correctamente',
      );
      onClose();
    } catch (error) {
      console.error('Error updating location:', error);
      showAlert(
        dispatch,
        'error',
        'Error al actualizar la información de ubicación',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ pt: 1 }}>
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              value={findOptionByName(regions, formData.region)}
              onChange={(event, newValue) =>
                setFormData((prev) => ({ ...prev, region: newValue?.name }))
              }
              options={regions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) =>
                option.name === value?.name
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Región"
                  variant="outlined"
                  placeholder="Seleccionar región"
                />
              )}
              sx={{ width: '100%' }}
              clearOnBlur
              selectOnFocus
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Autocomplete
              value={findOptionByName(provinces, formData.province)}
              onChange={(event, newValue) =>
                setFormData((prev) => ({ ...prev, province: newValue?.name }))
              }
              options={provinces}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) =>
                option.name === value?.name
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Provincia"
                  variant="outlined"
                  placeholder="Seleccionar provincia"
                />
              )}
              sx={{ width: '100%' }}
              clearOnBlur
              selectOnFocus
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Autocomplete
              value={findOptionByName(communes, formData.commune)}
              onChange={(event, newValue) =>
                setFormData((prev) => ({ ...prev, commune: newValue?.name }))
              }
              options={communes}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) =>
                option.name === value?.name
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Comuna"
                  variant="outlined"
                  placeholder="Seleccionar comuna"
                />
              )}
              sx={{ width: '100%' }}
              clearOnBlur
              selectOnFocus
            />
          </Box>

          <TextField
            fullWidth
            label="Dirección"
            value={formData.address ?? ''}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, address: e.target.value }))
            }
            margin="normal"
            variant="outlined"
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <CustomButton onClick={onClose} disabled={loading} color="error">
          Cancelar
        </CustomButton>
        <CustomButton onClick={handleSave} disabled={loading}>
          {loading ? 'Guardando...' : 'Guardar'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default LocationEditDialog;
